


import * as $ from 'jquery';

import * as L from 'leaflet';

import {
  LatLngBounds, Layer,
  LeafletEvent,
  LeafletKeyboardEvent,
  LeafletMouseEvent, Map,
  Marker,
  ResizeEvent,
  Point
} from 'leaflet';
import {Logger} from "@/util/logger";
import {Panorama} from "@/models/panorama";


/**
 *
 * @class ViewingCone
 *
 * Custom layer for use with LeafletJS - to show a viewing direction or "view cone" for
 * the current panorama displayed in KR Pano.
 *
 *   ref: https://leafletjs.com/examples/extending/extending-2-layers.html
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2022
 *
 */
export class ViewingCone extends L.Layer {

  /** the panorama the mini-map is focused on (i.e. current panorama in KR Pano) */
  private _panorama: Panorama           = null;

  /** bounding rect for the overlay layer */
  private _bounds: L.Bounds             = null;

  /** the canvas we draw our viewing cone on */
  private _container: HTMLCanvasElement = null;

  /** the (angle) direction of travel for the truck at this panorama */
  private _bearing: number              = 0;

  /** the (horizontal) angle the view has twitched the KR Pano view to */
  private _offset: number               = 0;

  /**
   *
   * standard constructor, you have to provide the current panorama, so we have a
   * position to draw the viewing cone at, and knw the viewing bearing.
   *
   */
  constructor(p: Panorama, bearing: number = 0, offset: number = 0) {

    super();

    let vc: ViewingCone = this;

    vc._panorama = p;
    vc._bearing  = bearing;
    vc._offset   = offset;

    setTimeout(() => {

      vc.findBearing();

    }, 50);

  }

  /** fetch the current direction of travel (of the railway track */
  bearing(): number {

    return this._bearing;
  }

  /** fetch the current view direction (offset from direction of travel */
  offset(): number {

    return this._offset;
  }

  /** find the direction of travel */
  findBearing() {

    let vc: ViewingCone = this;

    if (!vc._map) {

      /* nothing we can do yet */

      return ;
    }

    /*
     * we can't use the bearing or "heading" that the panorama has as an attribute. That
     * bearing is magnetic, and will be offset by magnetic declination from true north...to
     * some random amount, depending on what the location is.  We don't have a way of
     * reliably adjusting that to true north (not without integrating with some kind of
     * SDK for that), so we can't use it...too random.
     *
     * However, we can form a line from the previous to current position or from the
     * current position to the next position.  We have to have at least one of them, the
     * truc captures panoramas in sequences as it rolls down the track.  The "bend"
     * in a railway track over the 5-10m that the truck takes snapshots at...is minimal,
     * so just that line as our direction of travel.
     *
     * We can use this bearing as the "0" degrees offset, i.e. the direction the truck
     * is facing.  So, when the panorama view (i.e. KR Pano), rotates around, we can just
     * use that rotation as an offset to the bearing; because in KR Pano, "0" degrees
     * is the front of the truck.
     *
     * But first, we need our direction of travel...
     *
     */

    vc._bearing       = 0;

    let a: Panorama   = null;
    let b: Panorama   = null;

    if (vc._panorama.previous && vc._panorama.next) {

      /*
       * if we have both then line should be from previous to next, and we
       * assume that they are time ordered, with next in the future.
       *
       */

      a = vc._panorama.previous;
      b = vc._panorama.next;

    } else {

      /* similar but we draw the line from us to next, or from previous to us */

      if (vc._panorama.previous) {

        a = vc._panorama.previous;
        b = vc._panorama;

      } else if (vc._panorama.next) {

        a = vc._panorama;
        b = vc._panorama.next;

      }
    }

    if (!a || !b) {

      Logger.warning(`[ViewingCone] findBearing - missing both previous and next, can't find direction of travel.`);

      return ;
    }

    /* project a and b from lat/lng onto the screen, so we can work with simple 2D lines */

    let from: L.Point = vc._map.latLngToLayerPoint([a.y, a.x]);
    let to: L.Point   = vc._map.latLngToLayerPoint([b.y, b.x]);

    let px1: number   = from.x;
    let py1: number   = from.y;
    let px2: number   = to.x;
    let py2: number   = to.y;

    vc._bearing = Math.atan2(py1 - py2, px1 - px2) * 180 / Math.PI + 180;

    Logger.debug(`[ViewingCone] bearing from: ${a.id} to: ${b.id} bearing: ${vc._bearing}`);
  }

  /** once leaflet has added this layer to its map container, we can do our full setup */
  onAdd(map: L.Map): this {

    let vc: ViewingCone = this;

    Logger.debug(`[ViewingCone] onAdd() creating layer...`);

    vc.clear();

    vc._container = L.DomUtil.create(`canvas`, `viewconecanvas`);
    vc._container.id = 'viewconecanvas';

    L.DomUtil.setPosition(vc._container, new Point(0,0));

    vc.getPane().appendChild(vc._container);

    /* track when things like pan/zoom happen so we can reposition/refresh the viewing cone */

    map.on('viewreset', (e: LeafletEvent) => {

      vc._update();
    });

    map.on('zoomend', (e: LeafletEvent) => {

      vc._update();
    });

    map.on('moveend', (e: LeafletEvent) => {

      vc._update();

    });

    map.on('resize', (e: LeafletEvent) => {

      vc._update();

    });

    /* do the initial rendering */
    vc._update();

    return this;
  }

  clear() {

    let vc: ViewingCone = this;

    if (!vc._container) {

      return ;
    }

    L.DomUtil.remove(vc._container);

    delete vc._container;

    vc._container = null;
  }

  onRemove(map: L.Map): this {

    let vc: ViewingCone = this;

    Logger.debug(`[ViewingCone] onRemove() removing layer...`);

    vc.clear();

    return this;
  }

  /** fetch the canvase */
  canvas(): HTMLCanvasElement {

    return this._container;
  }

  /** convert to radians from degrees */
  radians(degrees: number): number {

    if (degrees < 0) {

      degrees = 360 + degrees;
    }

    return degrees * (Math.PI / 180.0);
  }

  /** update the viewing one with a viewing direction of the user */
  setOffset(offset: number) {

    let vc: ViewingCone = this;

    if (offset == vc._offset) {

      /* already set */

      return ;
    }

    vc._offset = offset;

    vc._update();
  }

  /** (re) render the layer */
  _update() {

    let vc: ViewingCone = this;

    if (!vc._map) {

      /* nothing to do */

      return ;
    }

    vc.findBearing();

    /*
     * when leaflet pans or zooms, it moves layers around.  It has to do this
     * to center things, not just for panning because you can pan and zoom at
     * the same time, like zooming in on the mouse's location.   So, on any
     * update you have to move your custom layer to align with where leaflet
     * itself is now positioned.
     *
     * Fortunately for us, this is relatively easy; all we want is a canvas
     * that fills the minimap window completely, but...its co-originates have
     * to start in the upper left corner of where leaflet starts.  Every time
     * we need to update, we just setPosition() ourselves to the north-west
     * corner of the view port that leaflet is using.
     *
     */

    let nw = vc._map.latLngToLayerPoint(vc._map.getBounds().getNorthWest());

    L.DomUtil.setPosition(vc._container, nw);

    /*
     * our viewing cone is anchored to the current panorama, but after zooming
     * and panning, that anchor point has moved by some offset from where the
     * window is centered, or rather...the view port moved to re-center somehow,
     * the anchor itself never moves.  So, we have to figure out the offset
     * from the center of the view port, and then offset ourselves when drawing
     * stuff...so that we are always drawing relative to our anchor point.
     *
     * While we are at it, we use the bounds of the view port to figure the
     * width and height of the canvas, this is not the most direct way, but, it
     * allows us to cover all the bases for resizes, view changes etc.  Without
     * setting the canvas dimensions, it will have the wrong aspect ratio.
     *
     */

    const lat: number                   = vc._panorama.x;
    const lng: number                   = vc._panorama.y;

    let src                             = vc._map.latLngToLayerPoint([lng, lat]);
    let dst                             = vc._map.latLngToLayerPoint(vc._map.getCenter());


    const ctx: CanvasRenderingContext2D = vc.canvas().getContext('2d');
    const c: HTMLCanvasElement          = ctx.canvas;

    let ne                              = vc._map.latLngToLayerPoint(vc._map.getBounds().getNorthEast());
    let sw                              = vc._map.latLngToLayerPoint(vc._map.getBounds().getSouthWest());

    let vw: number                      = ne.x - sw.x;
    let vh: number                      = sw.y - ne.y;

    c.width                             = vw;
    c.height                            = vh;

    /*
     * the offset of our anchor is simply where we translated the upper corner,
     * because without any movement, it would have been (0,0)
     *
     */

    let xOff: number   = nw.x;
    let yOff: number   = nw.y;

    /* clear the canvas entirely */

    ctx.clearRect(0, 0, c.width, c.height);

    /* set the anchor point for our graphic */

    let cx: number     = src.x - xOff;
    let cy: number     = src.y - yOff;

    /*
     * draw a "pie slice" by creating a path from center to the arc of
     * a pie slice and back again.
     *
     */

    let angle: number  = vc._bearing + vc._offset;
    let radius: number = 60;

    Logger.debug(`[ViewingCone] _update - view bearing: `, angle);

    let sa: number     = vc.radians(angle - 30 );
    let se: number     = vc.radians(angle + 30 );

    let gradient       = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);

    gradient.addColorStop(0, 'rgba(0,123,255,1)');
    gradient.addColorStop(1, 'rgba(0,123,255,0)');

    ctx.fillStyle      = gradient;

    ctx.moveTo(cx,cy);
    ctx.arc(cx, cy, radius, sa, se);
    ctx.lineTo(cx, cy);
    ctx.fill();
    ctx.closePath();

  }

}
